import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllEvents } from "../../../redux/actions/authActions";
import { getAllCompliances } from "../../../redux/actions/authActions";
import { Calendar, Modal } from "antd";
import { AiOutlineEye } from "react-icons/ai";
import dayjs from "dayjs";

// You can move this to a shared constants file if you use it elsewhere
const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Lakshadweep",
  "Puducherry",
];

const CompliancesView = () => {
  const dispatch = useDispatch();

  // Redux selectors for data needed by this component
  const { allEvents = [] } = useSelector((state) => state.auth);
  const { compliances: allCompliances = [] } = useSelector(
    (state) => state.auth
  );

  // Local state for managing the calendar and modals
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [eventDetails, setEventDetails] = useState([]);
  const [compliances, setCompliances] = useState([]);
  const [selectedState, setSelectedState] = useState("All States");

  // State for modals
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [modalEvent, setModalEvent] = useState(null);
  const [isCompModalOpen, setIsCompModalOpen] = useState(false);
  const [modalComp, setModalComp] = useState(null);

  // Fetch required data on component mount
  useEffect(() => {
    dispatch(getAllEvents());
    dispatch(getAllCompliances());
  }, [dispatch]);

  // Filter events and compliances whenever the selected date or state filter changes
  useEffect(() => {
    const filteredEvents = allEvents.filter(
      (event) => dayjs(event.date).format("YYYY-MM-DD") === selectedDate
    );

    const stateFilteredCompliances =
      selectedState === "All States"
        ? allCompliances
        : allCompliances.filter(
            (c) => c.state === selectedState || c.state === "All States"
          );

    const filteredCompliances = stateFilteredCompliances.filter(
      (c) => dayjs(c.dueDate).format("YYYY-MM-DD") === selectedDate
    );

    setEventDetails(filteredEvents);
    setCompliances(filteredCompliances);
    console.log("filtered", filteredCompliances);
  }, [selectedDate, allEvents, allCompliances, selectedState]);

  // --- Handlers ---

  const handleDateSelect = (date) => {
    setSelectedDate(date.format("YYYY-MM-DD")); // Store as string
  };

  const handleEventClick = (event) => {
    setModalEvent(event);
    setIsEventModalOpen(true);
  };

  const handleCompClick = (comp) => {
    setModalComp(comp);
    setIsCompModalOpen(true);
  };

  const handleEventModalCancel = () => {
    setIsEventModalOpen(false);
    setModalEvent(null);
  };

  const handleCompModalCancel = () => {
    setIsCompModalOpen(false);
    setModalComp(null);
  };

  // --- Calendar Cell Rendering ---

  const dateCellRender = useCallback(
    (value) => {
      const dateStr = value.format("YYYY-MM-DD");

      const hasEvent = allEvents.some(
        (event) => dayjs(event.date).format("YYYY-MM-DD") === dateStr
      );

      const dueCompliances = allCompliances.filter(
        (c) => dayjs(c.dueDate).format("YYYY-MM-DD") === dateStr
      );

      const complianceCount = dueCompliances.length;
      const dotSize = Math.min(6 + complianceCount * 2, 16);

      return (
        <div className="flex flex-col items-center">
          {hasEvent && (
            <div
              className="w-1.5 h-1.5 rounded-full bg-blue-500 my-0.5"
              title="Event"
            />
          )}
          {complianceCount > 0 && (
            <div
              className="rounded-full bg-red-500 my-0.5"
              style={{ width: `${dotSize}px`, height: `${dotSize}px` }}
              title={`${complianceCount} Compliance${
                complianceCount > 1 ? "s" : ""
              } Due`}
            />
          )}
        </div>
      );
    },
    [allEvents, allCompliances]
  );

  return (
    <div className="p-6  bg-gray-50 min-h-screen">
      <h2 className="text-2xl font-bold text-gray-800 ">Calendar</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Calendar Widget */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <Calendar
            onSelect={handleDateSelect}
            cellRender={dateCellRender}
            value={dayjs(selectedDate)} // Convert back to dayjs for Calendar
          />
        </div>

        {/* Selected Date Details */}
        <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col h-screen overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">
              Details for{" "}
              <span className="text-blue-600">
                {dayjs(selectedDate).format("MMMM D, YYYY")}
              </span>
            </h3>
            <div className="flex items-center space-x-2">
              <span className="flex-grow text-right text-xs text-gray-500">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 mr-1" />
                Events
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 ml-3 mr-1" />
                Compliances
              </span>
              <select
                id="state-filter-calendar"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="form-select block w-32 pl-3 pr-10 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="All States">All States</option>
                {indianStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto pr-2">
            {/* Events for selected date */}
            {eventDetails.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-700 text-lg">
                  Events ({eventDetails.length})
                </h4>
                {eventDetails.map((event) => (
                  <div
                    key={event._id}
                    className="bg-gray-100 rounded-lg p-4 flex justify-between items-start cursor-pointer hover:bg-blue-50 transition-colors duration-200"
                    onClick={() => handleEventClick(event)}
                  >
                    <div>
                      <div className="text-sm font-semibold">{event.name}</div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {event.description}
                      </p>
                    </div>
                    <AiOutlineEye className="text-gray-500 hover:text-blue-600 transition-colors" />
                  </div>
                ))}
              </div>
            )}

            {/* Compliances for selected date */}
            {compliances.length > 0 && (
              <div className="space-y-4 mt-6">
                <h4 className="font-semibold text-gray-700 text-lg">
                  Compliances Due ({compliances.length})
                </h4>
                {compliances.map((comp) => (
                  <div
                    key={comp._id}
                    className="bg-gray-100 rounded-lg p-4 flex justify-between items-start cursor-pointer hover:bg-red-50 transition-colors duration-200"
                    onClick={() => handleCompClick(comp)}
                  >
                    <div>
                      <div className="text-sm font-semibold">{comp.act}</div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {comp.state}
                      </p>
                    </div>
                    <AiOutlineEye className="text-gray-500 hover:text-red-600 transition-colors" />
                  </div>
                ))}
              </div>
            )}

            {/* No items for date */}
            {eventDetails.length === 0 && compliances.length === 0 && (
              <p className="text-gray-500 text-center py-8">
                No events or compliances for this date.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* --- Modals --- */}
      <Modal
        title="Event Details"
        open={isEventModalOpen}
        onCancel={handleEventModalCancel}
        footer={null}
        destroyOnClose
      >
        {modalEvent && (
          <div>
            <h3 className="text-lg font-semibold mb-2">{modalEvent.name}</h3>
            <p className="mb-2">{modalEvent.description}</p>
            <p className="text-sm text-gray-600">
              Date: {new Date(modalEvent.date).toDateString()}
            </p>
            {modalEvent.document && (
              <a
                className="text-blue-500 mt-2 inline-block"
                href={modalEvent.document}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Document
              </a>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="Compliance Details"
        open={isCompModalOpen}
        onCancel={handleCompModalCancel}
        footer={null}
        destroyOnClose
      >
        {modalComp && (
          <div>
            <h3 className="text-lg font-semibold mb-2">{modalComp.act}</h3>
            <p className="mb-2">{modalComp.description}</p>
            <p className="mb-2">State : {modalComp.state}</p>
            <p className="mb-2">Act-Type : {modalComp.actType}</p>
            <p className="mb-2">
              Remittance / Return : {modalComp["remittance/Return"]}
            </p>
            <p className="mb-2">Type of return : {modalComp.typeOfReturn}</p>
            <p className="mb-2">Remarks : {modalComp.remarks1 || "None"} </p>
            <p className="text-sm text-gray-600">
              Due Date: {new Date(modalComp.dueDate).toDateString()}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CompliancesView;
